import React from "react";
import { Divider, Grid, Typography } from "@material-ui/core";
import PropTypes from "prop-types";

import { formatToCurrency } from "utils/number";
import { reconciliationItemShape } from "constants/reconciliationItemShape";

const getFieldValue = (
  reconciliation,
  reconciliationDataFieldName,
  isNegative
) => {
  if (
    !reconciliation ||
    reconciliation[reconciliationDataFieldName] === undefined
  ) {
    return "";
  }

  return formatToCurrency(
    (isNegative ? -1 : 1) * reconciliation[reconciliationDataFieldName]
  );
};

const ReconciliationRow = ({
  displayName,
  reconciliationDataFieldName,
  reconciliationData,
  classes,
  insertDivider,
  bold,
  underlined,
  isNegative,
  hasLeftMargin,
  hasVerticalSpacing,
  enableVarianceHighlight,
}) => {
  const categories = ["audited", "current", "changes", "variance"];

  const isVarianceNonZero =
    enableVarianceHighlight &&
    reconciliationData?.variance?.[reconciliationDataFieldName] !== 0 &&
    reconciliationData?.variance?.[reconciliationDataFieldName] !== undefined;

  return (
    <>
      {insertDivider ? (
        <Divider className={classes.rowDivider} />
      ) : (
        <div style={{ marginTop: 8, marginBottom: 8 }} />
      )}
      <Grid
        container
        direction="row"
        wrap="nowrap"
        className={hasVerticalSpacing ? classes.verticalMargins : undefined}
      >
        {reconciliationDataFieldName !== undefined ? (
          <>
            <Grid item className={classes.firstColumnCell}>
              <Typography
                variant={bold ? "body2" : "body1"}
                className={underlined ? classes.underlined : undefined}
                style={{
                  marginLeft: hasLeftMargin ? 24 : 0,
                  color: isVarianceNonZero ? "#E05343" : undefined,
                }}
              >
                {displayName}
              </Typography>
            </Grid>

            <Grid item className={classes.lastColumnCell} align="right">
              <Typography
                variant={bold ? "body2" : "body1"}
                className={underlined ? classes.underlined : undefined}
                style={{
                  marginRight:
                    !isNegative ||
                    (reconciliationData?.[reconciliationDataFieldName] ?? 0) ===
                      0
                      ? 4
                      : 0,
                  color: isVarianceNonZero ? "#E05343" : undefined,
                }}
              >
                {getFieldValue("fff", reconciliationDataFieldName, isNegative)}
              </Typography>
            </Grid>

            <Grid item className={classes.middleColumnSpacing} />

            {categories.map((category) => (
              <Grid
                item
                className={classes.cell}
                key={`${category}-${reconciliationDataFieldName}`}
                align="right"
              >
                <Typography
                  variant={bold ? "body2" : "body1"}
                  className={underlined ? classes.underlined : undefined}
                  style={{
                    marginRight:
                      !isNegative ||
                      (reconciliationData?.[category]?.[
                        reconciliationDataFieldName
                      ] ?? 0) === 0
                        ? 4
                        : 0,
                    color: isVarianceNonZero ? "#E05343" : undefined,
                  }}
                >
                  {getFieldValue(
                    reconciliationData?.[category],
                    reconciliationDataFieldName,
                    isNegative
                  )}
                </Typography>
              </Grid>
            ))}
          </>
        ) : (
          <Grid item className={classes.firstColumnCell}>
            <Typography
              variant={bold ? "h5" : "body1"}
              className={underlined ? classes.underlined : undefined}
              style={{
                color: isVarianceNonZero ? "#E05343" : undefined,
              }}
            >
              {displayName}
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  );
};

ReconciliationRow.propTypes = {
  displayName: PropTypes.string.isRequired,
  reconciliationDataFieldName: PropTypes.string,
  reconciliationData: PropTypes.shape({
    audited: reconciliationItemShape,
    current: reconciliationItemShape,
    changes: reconciliationItemShape,
    variance: reconciliationItemShape,
    meta: PropTypes.shape({
      isAudited: PropTypes.bool,
    }),
  }),
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  insertDivider: PropTypes.bool,
  bold: PropTypes.bool,
  underlined: PropTypes.bool,
  isNegative: PropTypes.bool,
  hasLeftMargin: PropTypes.bool,
  hasVerticalSpacing: PropTypes.bool,
  enableVarianceHighlight: PropTypes.bool,
};

ReconciliationRow.defaultProps = {
  reconciliationDataFieldName: undefined,
  reconciliationData: undefined,
  insertDivider: false,
  bold: false,
  underlined: false,
  isNegative: false,
  hasLeftMargin: true,
  hasVerticalSpacing: false,
  enableVarianceHighlight: false,
};

export default ReconciliationRow;
